import {
  DividerPhoto,
  Grid,
  GridSize,
  GridColumn,
  Hero,
  HeroCaseVideoCarousel,
  HeroVideoSlider,
  Video,
  Marquee,
  BlogPostGrid,
  BlogPostHeader,
  BlogPostBody,
  BlogPostReadMore,
  BlogCategoryPostGrid,
  BlogAuthorPostGrid,
} from '@data/sanity/schema'
import { Keyed } from '@lib/helpers'
import { SanityBlocks } from './blocks'
import {
  SanityBlogAuthor,
  SanityBlogCategory,
  SanityBlogPostOptions,
  SanityBlogPostWithoutBody,
} from './blog'
import { SanityContentFragment } from './content'
import { SanityBorderRadius, SanityImageFragment } from './image'
import { SanityLinkReference } from './link'
import { SanityMuxVideo, SanityVideo, SanityVideoAspectRatio } from './video'

export enum SanityModuleType {
  GRID = 'grid',
  HERO = 'hero',
  HERO_CASE_VIDEO_CAROUSEL = 'heroCaseVideoCarousel',
  HERO_VIDEO_SLIDER = 'heroVideoSlider',
  DIVIDER_PHOTO = 'dividerPhoto',
  LARGE_MEDIA_TEXT = 'largeMediaText',
  CASE_OVERVIEW = 'caseOverview',
  VIDEO = 'video',
  MARQUEE = 'marquee',
  BLOG_POST_GRID = 'blogPostGrid',
  BLOG_POST_HEADER = 'blogPostHeader',
  BLOG_POST_BODY = 'blogPostBody',
  BLOG_POST_READ_MORE = 'blogPostReadMore',
  BLOG_CATEGORY_POST_GRID = 'blogCategoryPostGrid',
  BLOG_AUTHOR_POST_GRID = 'blogAuthorPostGrid',
}

// Module component types
export enum SanityColor {
  WHITE = 'white',
  GRAY = 'gray',
  GREEN_LIGHT = 'green-light',
  GREEN = 'green',
  GREEN_ELECTRIC = 'green-electric',
  GREEN_DARK = 'green-dark',
  PINK_FADED = 'pink-faded',
  PINK = 'pink',
  PINK_HOT = 'pink-hot',
  BLUE = 'blue',
  RED = 'red',
  YELLOW = 'yellow',
}

export type SanityGridColumnSize = Pick<
  GridSize,
  | 'breakpoint'
  | 'width'
  | 'justify'
  | 'align'
  | 'start'
  | 'justifyContent'
  | 'alignContent'
>

export type SanityGridColumn = Keyed<
  Pick<GridColumn, 'spacing'> & {
    sizes: Array<SanityGridColumnSize>
    blocks: Array<SanityBlocks>
    background?: SanityColor
    frameColor?: SanityColor
    borderRadius?: SanityBorderRadius
    hideOverflow?: boolean
  }
>

export type SanityGrid = Pick<Grid, 'size' | 'reverseSequence' | 'spacing'> & {
  fullWidth?: boolean
  noColumnGaps?: boolean
  noRowGaps?: boolean
  background?: SanityColor
  columns: Array<SanityGridColumn>
}

export interface SanityHeroPhotos {
  mobilePhoto?: SanityImageFragment
  desktopPhoto?: SanityImageFragment
}

export enum SanityHeroBackgroundType {
  NONE = 'none',
  PHOTO = 'photo',
  MUX_VIDEO = 'mux-video',
}

export enum SanityHeroContentPosition {
  CENTER = 'center',
  BOTTOM_LEFT = 'bottom-left',
}

export interface SanityHero {
  bgType: SanityHeroBackgroundType
  contentPosition: SanityHeroContentPosition
  content?: SanityContentFragment
  showScrollIndicator?: boolean
  photos?: SanityHeroPhotos
  muxVideo?: SanityMuxVideo
  videoAspectRatio?: SanityVideoAspectRatio
}

export interface SanityHeroCaseVideoCarousel {
  items: Array<{
    content?: SanityContentFragment
    casePage?: SanityLinkReference & {
      heroModule?: SanityHero
    }
  }>
}

export interface SanityHeroVideoSlider {
  slides: Array<{
    content?: SanityContentFragment
    muxVideo?: SanityMuxVideo
  }>
}

export interface SanityDividerPhoto {
  background?: SanityColor
  photo: SanityImageFragment
}

export type SanityMarqueeSimpleItem = {
  _type: 'simple'
  text: string
}

export type SanityMarqueePhotoItem = {
  _type: 'photo'
  photo: SanityImageFragment
}

export type SanityMarqueeItem = Keyed<
  SanityMarqueeSimpleItem | SanityMarqueePhotoItem
>

export type SanityMarquee = Pick<Marquee, 'speed' | 'reverse' | 'pausable'> & {
  items: Array<SanityMarqueeItem>
  content?: SanityContentFragment
  contentAlignment?: 'left' | 'center' | 'right'
}

export interface SanityBlogPostGrid {
  postsPerRow?: number
  posts: Array<SanityBlogPostWithoutBody>
  author?: SanityBlogAuthor
  category?: SanityBlogCategory
  options?: SanityBlogPostOptions
}

export interface SanityBlogPostHeader {
  post?: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

export interface SanityBlogPostBody {
  content: SanityContentFragment
}

// export interface SanityBlogPostReadMore {}

export type SanityBlogCategoryPostGrid = Pick<SanityBlogPostGrid, 'options'>

export type SanityBlogAuthorPostGrid = Pick<SanityBlogPostGrid, 'options'>

// Module data types
export type SanityGridModule = Keyed<Pick<Grid, '_type'> & SanityGrid>

export type SanityHeroModule = Keyed<Pick<Hero, '_type'> & SanityHero>

export type SanityHeroCaseVideoCarouselModule = Keyed<
  Pick<HeroCaseVideoCarousel, '_type'> & SanityHeroCaseVideoCarousel
>

export type SanityHeroVideoSliderModule = Keyed<
  Pick<HeroVideoSlider, '_type'> & SanityHeroVideoSlider
>

export type SanityDividerPhotoModule = Keyed<
  Pick<DividerPhoto, '_type'> & SanityDividerPhoto
>

export type SanityVideoModule = Keyed<Pick<Video, '_type'> & SanityVideo>

export type SanityMarqueeModule = Keyed<Pick<Marquee, '_type'> & SanityMarquee>

export type SanityBlogPostGridModule = Keyed<
  Pick<BlogPostGrid, '_type'> & SanityBlogPostGrid
>

export type SanityBlogPostHeaderModule = Keyed<
  Pick<BlogPostHeader, '_type'> & SanityBlogPostHeader
>

export type SanityBlogPostBodyModule = Keyed<
  Pick<BlogPostBody, '_type'> & SanityBlogPostBody
>

export type SanityBlogPostReadMoreModule = Keyed<
  Pick<BlogPostReadMore, '_type'>
>

export type SanityBlogCategoryPostGridModule = Keyed<
  Pick<BlogCategoryPostGrid, '_type'> & SanityBlogCategoryPostGrid
>

export type SanityBlogAuthorPostGridModule = Keyed<
  Pick<BlogAuthorPostGrid, '_type'> & SanityBlogAuthorPostGrid
>

export interface SanityLargeMediaTextModule {
  _key: string
  _type: 'largeMediaText'
  mediaType: 'image' | 'video'
  image?: SanityImageFragment
  video?: SanityMuxVideo
  content: SanityContentFragment
  frameColor?: SanityColor
}

export interface SanityTag {
  label: string
  value: string
}

type SanityCaseCoverMediaType = 'photo' | 'mux-video'

export interface SanityCaseOverviewCase {
  title: string
  slug: string
  coverMediaType?: SanityCaseCoverMediaType
  coverImage?: SanityImageFragment
  coverVideo?: SanityMuxVideo
  tags?: SanityTag[]
}

export interface SanityCaseOverviewModule {
  _key: string
  _type: 'caseOverview'
  title?: string
  filterByTags?: boolean
  cases: SanityCaseOverviewCase[]
}

export type SanityModule =
  | SanityGridModule
  | SanityHeroModule
  | SanityHeroCaseVideoCarouselModule
  | SanityHeroVideoSliderModule
  | SanityDividerPhotoModule
  | SanityVideoModule
  | SanityMarqueeModule
  | SanityBlogPostGridModule
  | SanityBlogPostHeaderModule
  | SanityBlogPostBodyModule
  | SanityBlogPostReadMoreModule
  | SanityBlogCategoryPostGridModule
  | SanityBlogAuthorPostGridModule
  | SanityLargeMediaTextModule
  | SanityCaseOverviewModule
